import { CheckCircleIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { useMemo } from 'react';
import { get } from '@/renderer/utils/get';
import calculatePrice from './calculatePrice';

type MPO = API.MembershipProductOffer;

const BASE_STATS: API.MembershipProductStats = {
  canceled: 0,
  loss: 0,
  noAction: 0,
  pending: 0,
  played: 0,
  push: 0,
  units: 0,
  win: 0,
  buyable: 0,
  charged: 0,
  roi: 0,
  total: 0,
};

const BASE_OFFER: MPO = {
  id: 0,
  ids: [0],
  plays: 0,
  price: 0,
};

type IncludeItem = string | { text?: string; href?: string; linkText?: string };

export interface TemplateContext {
  product: API.MembershipProduct;
  stats: Record<string, unknown>;
  calc: { plays?: number; [k: string]: unknown };
}

/** Lightweight version of ProductPage's nestedReplace — interpolates {product.*}, {stats.*}, {calc.*}
 *  template variables (with || fallbacks) used in product.ui.includes. */
export function fillTemplate(str: string | undefined, ctx: TemplateContext): string {
  if (!str) return str ?? '';
  let cur = str;
  let next = str;

  do {
    cur = next;
    next = next.replace(/{([^{}]+)}/g, (_match, inner) => {
      const parts = inner.split('||').map((p: string) => p.trim().replace(/^["']|["']$/g, ''));

      for (const part of parts) {
        const value = (() => {
          if (part === '') return '';
          // Literal fallback text (no leading letter / not a path)
          if (!/[.[\]]/.test(part) && !/^[a-zA-Z_]/.test(part)) return part;

          const [root, ...rest] = part.split('.');
          const path = rest.join('.');

          if (root === 'product') return get(ctx.product, path, undefined);
          if (root === 'stats') return get(ctx.stats, path, undefined);
          if (root === 'calc') {
            if (path === 'plays' && (ctx.calc?.plays ?? 0) > 300) return '';
            return get(ctx.calc, path, undefined);
          }
          return undefined;
        })();

        if (value !== undefined && value !== null && value !== '') {
          return value as string;
        }
      }

      return '';
    });
  } while (next !== cur);

  return next;
}

export function buildTemplateContext(product: API.MembershipProduct): TemplateContext {
  const defaultOffer =
    product?.offers?.find((o) => o.ui?.isDefault) ?? product?.offers?.[0] ?? BASE_OFFER;
  const calc = calculatePrice({
    discount: 0,
    stats: product?.stats || BASE_STATS,
    variety: defaultOffer,
  });

  // Stats summary — prefer curated resultSummaries[0], else product.stats
  const summary = product?.ui?.resultSummaries?.[0];
  const summaryParts = (summary?.value || '').split('-').map(Number);
  const w = Number.isFinite(summaryParts[0]) ? summaryParts[0] : (product?.stats?.win ?? 0);
  const l = Number.isFinite(summaryParts[1]) ? summaryParts[1] : (product?.stats?.loss ?? 0);
  const t = w + l;
  const pct = t > 0 ? (w / t) * 100 : 0;
  const u =
    summary?.units != null
      ? parseFloat(String(summary.units))
      : typeof product?.stats?.units === 'number'
        ? product.stats.units
        : 0;

  return {
    product,
    calc,
    stats: {
      wins: w,
      losses: l,
      total: t,
      record: `${w}–${l}`,
      winPct: `${pct.toFixed(1)}%`,
      units: `${u >= 0 ? '+' : ''}${u.toFixed(2)}u`,
    },
  };
}

/** Renders a product's ui.includes list with template interpolation and link support.
 *  `theme` controls text colors: 'light' supports light+dark mode; 'dark' is always-dark
 *  (for the marketing pages that render on a permanent dark background). */
export default function ProductIncludes({
  product,
  theme = 'light',
  className,
}: {
  product?: API.MembershipProduct;
  theme?: 'light' | 'dark';
  className?: string;
}) {
  const ctx = useMemo(() => (product ? buildTemplateContext(product) : null), [product]);
  const includes: IncludeItem[] = product?.ui?.includes ?? [];

  if (!ctx || includes.length === 0) return null;

  const textCls = theme === 'dark' ? 'text-zinc-300' : 'text-zinc-600 dark:text-zinc-300';
  const linkCls =
    theme === 'dark'
      ? 'ml-1 font-medium text-white underline hover:text-zinc-300'
      : 'ml-1 font-medium text-zinc-900 underline hover:text-zinc-600 dark:text-white dark:hover:text-zinc-300';

  return (
    <ul className={clsx('flex flex-col gap-2.5', className)}>
      {includes.map((item, i) => {
        const rawText = typeof item === 'string' ? item : (item.text ?? '');
        const text = fillTemplate(rawText, ctx);
        const href = typeof item === 'string' ? null : (item.href ?? null);
        const linkText = typeof item === 'string' ? null : (item.linkText ?? null);
        return (
          <li key={i} className={clsx('flex items-start gap-2.5 text-sm leading-snug', textCls)}>
            <CheckCircleIcon className="mt-0.5 size-4 shrink-0 text-emerald-500" />
            <span>
              {text}
              {href && (
                <a href={href} target="_blank" rel="noreferrer" className={linkCls}>
                  {linkText || href}
                </a>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
